import React, { useEffect } from 'react';

import { EventHub }         from './EventHub';
import EventType            from './EventType';
import { IEventHubContext } from './IEventHubContext';

type Props = {
  eventHub: EventHub;
};

const EventHubContext = React.createContext<IEventHubContext>({
  eventHub: undefined,
});

/**
 * The `EventHubProvider` takes an instance of the {EventHub} via a `prop`,
 * and passes that down using the context API.
 *
 * @param eventHub The {EventHub} instance to make available to descendant components.
 * @param children A node (or array of nodes) to render within the provider.
 *
 * @returns A reference to the `EventHubProvider`.
 */
const EventHubProvider: React.FC<Props> = ({ eventHub, children }) => (
  <EventHubContext.Provider value={{ eventHub }}>
    {children}
  </EventHubContext.Provider>
);

/**
 * The `useEventHub` hook gives you access to the {EventHub} instance that
 * you may use to subscribe and sent events.
 *
 * @returns A reference to the underlying {EventHub} instance.
 */
const useEventHub = () => {
  const eventHubContext = React.useContext<IEventHubContext>(EventHubContext);

  if (eventHubContext === undefined) {
    throw new Error('`useEventHub` must be used within an EventHubProvider.');
  }

  return eventHubContext;
};

/**
 * The `useEventSubscription` hook wires up a subscription handler to the
 * underlying {EventHub} instance. This hook manages the `subscribe` and
 * `unsubscribe` side effects for you.
 *
 * @param topic The topic the subscription handler will subscribe to.
 * @param subscription The subscription handler.
 */
const useEventSubscription = (
  topic: EventType,
  subscription: (data: any) => void,
) => {
  const { eventHub } = useEventHub();

  useEffect(() => {
    const sub = (eventHub as EventHub).subscribe(topic, subscription);
    return () => sub.unsubscribe();
  }, [eventHub, topic, subscription]);
};

export {
  EventHubProvider, EventHubContext, useEventHub, useEventSubscription,
};

export type { IEventHubContext };
